import React, { useEffect, useMemo, useState } from "react";
import { Loader, PlusCircle, Upload, Star } from "react-feather";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import SideForm from "../../../components/sideForm";
import { IApplicationState } from "../../../redux/reducers";
import { routes } from "../../routes";
import AddIngredientForm from "../../ingredients/ingredientDetail/form/addForm";
import ReactImageFallback from "react-image-fallback";
import config from "../../../utils/config";
import { getIngredientImagePlaceholder } from "../../../utils/ingredient";
import { toCamelCase } from "../../../utils/string";
import { IIngredient } from "../../../interfaces/ingredient";
import { IPaginateList } from "../../../interfaces/pagination";
import { Bar } from "react-chartjs-2";
import UpgradeOverlay from "../../../components/common/UpgradeOverlay";
import { hasSupplierManufacturerAccess } from "../../../utils/subscription";
import { ViewMode } from "../../../redux/actions/user/userAction";
import ingredientService from "../../../services/ingredientService";
import { paginateAction } from "../../../redux/actions/pagination/pagination";
import { ingredients } from "../../../redux/actions/actionContants";
import { useDebounce } from "../../../utils/hooks";
import "../../../assets/scss/pages/_supplierDashboard.scss";

interface IProps extends RouteComponentProps {
  history: any;
  starIngredients: IIngredient[];
  ingredients: IPaginateList<IIngredient[]>;
  ingredientUsage: {
    labels: string[];
    activeCounts: number[];
    conceptCounts: number[];
  };
  profile?: any;
  currentViewMode: ViewMode;
  fetchIngredients: (searchTerm?: string) => Promise<any>;
  paginate: (page: number) => void;
  setPaginationSize: (size: number, searchTerm?: string) => void;
}

const SupplierDashboard: React.FC<IProps> = ({
  history,
  starIngredients,
  ingredients: { list, pagination },
  ingredientUsage,
  profile,
  currentViewMode,
  fetchIngredients,
  paginate,
  setPaginationSize
}) => {
  const [addFormOpen, setAddFormOpen] = useState<boolean>(false);
  const [starredLoading, setStarredLoading] = useState<boolean>(false);
  const [ingredientLoading, setIngredientLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [searchLoading, setSearchLoading] = useState<boolean>(false);

  // Debounce search term to avoid excessive API calls
  const debouncedSearch = useDebounce(search, 500);

  const toggleAddForm = () => setAddFormOpen(!addFormOpen);

  // Bar Chart Data (unchanged)
  // const barChartData = {
  //   labels: ["Jul", "Aug", "Sep", "Oct"],
  //   datasets: [
  //     { label: "Active", data: [40, 55, 60, 80], backgroundColor: "#cbd5e1" },
  //     { label: "Concept", data: [70, 90, 90, 110], backgroundColor: "#1e293b" }
  //   ]
  // };
  const barChartData = useMemo(() => {
    if (!ingredientUsage) return { labels: [], datasets: [] };

    return {
      labels: ingredientUsage.labels, // e.g. months or categories
      datasets: [
        {
          label: "Active",
          data: ingredientUsage.activeCounts,
          backgroundColor: "#cbd5e1"
        },
        {
          label: "Concept",
          data: ingredientUsage.conceptCounts,
          backgroundColor: "#1e293b"
        }
      ]
    };
  }, [ingredientUsage]);

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [
        { ticks: { beginAtZero: true, fontColor: "#475569" }, gridLines: { color: "#e2e8f0" } }
      ],
      xAxes: [{ ticks: { fontColor: "#475569" }, gridLines: { display: false } }]
    },
    legend: { display: true, labels: { fontColor: "#475569", boxWidth: 14 } }
  };

  useEffect(() => {
    setStarredLoading(true);
    const t = setTimeout(() => setStarredLoading(false), 500);
    return () => clearTimeout(t);
  }, []);

  // Reset to first page when search term changes (before fetching)
  useEffect(() => {
    if (debouncedSearch !== undefined && pagination.page !== 1) {
      paginate(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Fetch ingredients on mount, when pagination changes, or when search term changes
  useEffect(() => {
    setSearchLoading(true);
    fetchIngredients(debouncedSearch).finally(() => {
      setSearchLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.size, debouncedSearch]);

  // Use server pagination values
  const currentPage = pagination?.page ?? 1;
  const pageSize = pagination?.size ?? 10;
  const totalItems = pagination?.total ?? 0;
  const totalPages = pagination?.pages ?? 1;

  // Calculate range for display
  const from = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalItems);

  // Use list directly (server-side filtering)
  const visibleRows = (list as IIngredient[]) || [];

  // Build compact page window (max 5 buttons)
  const getPageWindow = (current: number, total: number, windowSize = 5) => {
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(total, start + windowSize - 1);
    if (end - start + 1 < windowSize) start = Math.max(1, end - windowSize + 1);
    const arr: number[] = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  };
  const pageNumbers = getPageWindow(currentPage, totalPages, 5);

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      paginate(page);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    // setPaginationSize already resets to page 1 and fetches ingredients
    // Pass current search term to preserve search results
    setPaginationSize(newSize, debouncedSearch);
  };

  // Check subscription - fade data if not subscribed
  const isSubscribed = hasSupplierManufacturerAccess(profile);
  const contentClassName = isSubscribed ? "dashboard-content visible" : "dashboard-content faded";

  return (
    <div className="supplier-dashboard container-fluid py-4">
      <div className={`dashboard-content-wrapper ${contentClassName}`}>
        <h2 className="mb-4 fw-semibold">Welcome Back Supplier, </h2>

        {/* Search Bar */}
        <div className="row g-4 mb-3 align-items-center">
          <div className="col-12 col-md-8">
            <input
              type="search"
              className="form-control search-input"
              placeholder="Search ingredients, suppliers, and more"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="row g-4">
          {/* Ingredient Portfolio */}
          <div className="col-12">
            <div className="card shadow-sm rounded-3 h-100">
              <div className="card-body">
                <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
                  <h5 className="fw-semibold mb-0">Ingredient Portfolio</h5>
                  <button
                    className="btn btn-dark d-flex align-items-center"
                    onClick={toggleAddForm}
                  >
                    <PlusCircle size={18} className="me-2" /> Add Ingredient
                  </button>
                </div>

                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Ingredient Name</th>
                        <th>Usage</th>
                        <th>Certification</th>
                        <th>Datasheet</th>
                        <th>Price per kg (est)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchLoading ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            <Loader className="fa-spin me-2" size={16} />
                            {debouncedSearch && debouncedSearch.trim() ? "Searching..." : "Loading..."}
                          </td>
                        </tr>
                      ) : visibleRows?.length ? (
                        visibleRows.map((item) => {
                          const missingData =
                            !item.estimated_price ||
                            !item.datasheet_url ||
                            (item.certifications?.length ?? 0) === 0;
                          return (
                            <tr key={item.id} className={missingData ? "ingredient-issue-row" : ""}>
                              <td>
                                <div className="ingredient-name d-flex align-items-center">
                                  <ReactImageFallback
                                    src={
                                      item.image_url
                                        ? item.image_url
                                        : config.api.endpoints.ingredients.ingredientImage(
                                          item.jf_display_name
                                        )
                                    }
                                    fallbackImage={getIngredientImagePlaceholder()}
                                    alt="Ingredient"
                                    className="me-2 rounded"
                                    style={{ height: "32px", width: "32px" }}
                                  />
                                  <div>
                                    <span>{toCamelCase(item.jf_display_name)}</span>
                                    <br />
                                    <small className="text-muted">Sweet Valley Co.</small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="fw-semibold text-dark">
                                  {item.active_count} Active
                                </span>
                                <br />
                                <small className="text-muted">{item.concept_count} concept</small>
                              </td>
                              <td>
                                {item.certifications?.length ? (
                                  <span className="badge bg-success">
                                    {item.certifications.length}
                                  </span>
                                ) : (
                                  <span className="badge bg-secondary">0</span>
                                )}
                              </td>
                              <td>
                                {item.datasheet_url ? (
                                  <a
                                    href={item.datasheet_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-success fw-semibold"
                                  >
                                    <Upload size={16} className="me-1" /> View
                                  </a>
                                ) : (
                                  <span className="text-muted">No data</span>
                                )}
                              </td>
                              <td>
                                {item.estimated_price ? (
                                  <span className="fw-semibold">
                                    ${item.estimated_price.toFixed(2)}
                                  </span>
                                ) : (
                                  <span className="text-muted">No data</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            No ingredients available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mt-3">
                  {/* LEFT: "Showing items per page [select] 1–10 of 58044 items" */}
                  <div className="d-flex align-items-center gap-2 flex-wrap pager-left">
                    <span className="text-muted small pager-label">Showing items per page</span>
                    <select
                      className="form-select form-select-sm pager-select"
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
                    >
                      {[5, 10, 20, 50].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <span className="text-muted small pager-range">
                      {from || 0}-{to || 0} of {totalItems} items
                    </span>
                  </div>

                  {/* RIGHT: buttons */}
                  <nav aria-label="Ingredient pages">
                    <ul className="pagination pagination-sm mb-0 jf-pager">
                      {/* Previous */}
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button
                          className="page-link prev-next"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>

                      {/* Windowed page numbers */}
                      {pageNumbers.map((n) => (
                        <li key={n} className={`page-item ${currentPage === n ? "active" : ""}`}>
                          <button className="page-link" onClick={() => handlePageChange(n)}>
                            {n}
                          </button>
                        </li>
                      ))}

                      {/* Ellipsis + last page (only if our window doesn't already include the end) */}
                      {pageNumbers[pageNumbers.length - 1] < totalPages && (
                        <>
                          <li className="page-item disabled">
                            <span className="page-link ellipsis">…</span>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? "active" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(totalPages)}
                            >
                              {totalPages}
                            </button>
                          </li>
                        </>
                      )}

                      {/* Next */}
                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button
                          className="page-link prev-next"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>

          {/* Below Ingredient Portfolio */}
          <div className="row g-4 mt-4">
            {/* Starred Ingredients */}
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm rounded-3 h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-semibold mb-0 d-flex align-items-center">
                      <span className="me-2">⭐</span> Starred Ingredients
                    </h6>
                    <button className="btn btn-link p-0">
                      <i className="bi bi-three-dots"></i>
                    </button>
                  </div>

                  {starredLoading ? (
                    <Loader className="fa-spin" />
                  ) : starIngredients.length ? (
                    <ul className="list-unstyled mb-0 starred-list">
                      {starIngredients.map((item, idx) => (
                        <li
                          key={item.id}
                          className={`d-flex align-items-center justify-content-between p-2 mb-2 rounded ${idx === 1 ? "active-row" : ""
                            }`}
                          role="button"
                          onClick={() => history.push(`${routes.INGREDIENTS}/${item.id}`)}
                        >
                          <div className="d-flex align-items-center">
                            <ReactImageFallback
                              src={
                                item.image_url
                                  ? item.image_url
                                  : config.api.endpoints.ingredients.ingredientImage(
                                    item.jf_display_name
                                  )
                              }
                              fallbackImage={getIngredientImagePlaceholder()}
                              alt="Ingredient"
                              className="rounded me-3"
                              style={{ height: "36px", width: "36px" }}
                            />
                            <div>
                              <span className="fw-medium">{toCamelCase(item.jf_display_name)}</span>
                              <br />
                              <small className="text-muted">Sweet Valley Co.</small>
                            </div>
                          </div>
                          <Star
                            size={16}
                            style={{ color: idx === 1 ? "#a1a1aa" : "#f9b300" }}
                            fill={idx === 1 ? "none" : "#f9b300"}
                          />
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted small">No Ingredient is starred</p>
                  )}

                  <div className="card-footer text-center bg-light rounded-bottom mt-3">
                    <button className="btn btn-link fw-semibold">View all</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Ingredient Usage Analytics */}
            <div className="col-12 col-lg-6">
              <div className="card shadow-sm rounded-3 h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-semibold mb-0 d-flex align-items-center">
                      📊 Ingredient Usage Analytics
                    </h6>
                    <button className="btn btn-link p-0">Full Analytics</button>
                  </div>
                  {ingredientLoading ? (
                    <Loader className="fa-spin" />
                  ) : (
                    <div style={{ height: "250px" }}>
                      <Bar data={barChartData} options={barChartOptions} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SideForm Modal */}
        <SideForm isOpen={addFormOpen} title="Add Ingredient" toggleForm={toggleAddForm}>
          <AddIngredientForm toggleForm={toggleAddForm} />
        </SideForm>
      </div>
      {!isSubscribed && <UpgradeOverlay viewMode={currentViewMode} />}
    </div>
  );
};

const mapStateToProps = (state: IApplicationState) => ({
  subscription_type: state.user.profile?.company.subscription_type,
  starIngredients: state.ingredient.starred,
  ingredientUsage: state.ingredient.ingredientUsage,
  ingredients: state.ingredient.ingredients,
  profile: state.user.profile,
  currentViewMode: state.user.currentViewMode
});

const mapDispatchToProps = {
  fetchIngredients: (searchTerm?: string) => {
    // Use search endpoint when search term exists, otherwise use regular paginated ingredients
    if (searchTerm && searchTerm.trim()) {
      return ingredientService.searchIngredientsForSupplier(searchTerm);
    } else {
      return ingredientService.getPaginatedIngredients(false, undefined, undefined);
    }
  },
  paginate: (page: number) => (dispatch: Dispatch) => {
    dispatch(paginateAction(ingredients.PAGINATE_INGREDIENTS, page));
  },
  setPaginationSize: (size: number, searchTerm?: string) =>
    (ingredientService as any).setPaginationSize(size, searchTerm)
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SupplierDashboard));
